import {
  BRANDS,
  BUTTERLFY,
  DIFFERENTIATION,
  FILTER_PARAM,
  FILTERS,
  FILTERS_VERSION_PARAM,
  FILTERS_VERSION_V2,
  MARKETS,
  PERCEPTUAL,
  POINT_IN_TIME,
  DISTRIBUTION,
  QUESTION_OPTIONS,
  SECONDARY_BRANDS,
  SEGMENTS,
  URL_BY_BRAND,
} from "@/constant/constantsThrougoutProject";
import { ROUTES_CHILDREN_LOOKUP } from "@/routes/routes";
import type { FilterSelection } from "@interfaces/filters/domain";

export const buildNewURLQueryString = (
  result: Record<string, any>,
  mode: any = "POINT_IN_TIME",
  pathName?: any,
): URLSearchParams => {
  const updatedURL = new URLSearchParams();
  const currentPathDetail =
    ROUTES_CHILDREN_LOOKUP[pathName ? pathName : location.pathname];

  if (
    location.pathname === `/emotional/${URL_BY_BRAND}` &&
    mode === POINT_IN_TIME &&
    (result?.visualizationType === PERCEPTUAL ||
      result?.visualizationType === BUTTERLFY ||
      result?.visualizationType === DIFFERENTIATION)
  ) {
    mode = result.visualizationType;
  } else if (
    (location.pathname?.startsWith("/emotional") ||
      location.pathname?.startsWith("/advertising") ||
      location.pathname?.startsWith("/equity") ||
      location.pathname?.startsWith("/conversion") ||
      location.pathname?.startsWith("/visibility") ||
      location.pathname?.startsWith("/reputation")) &&
    mode === POINT_IN_TIME &&
    result?.visualizationType === DISTRIBUTION
  ) {
    mode = result.visualizationType;
  }

  currentPathDetail?.urlKeysMapping?.[mode]?.forEach((key: string) => {
    if (currentPathDetail?.multipleOptionsForKeys[mode]?.includes(key)) {
      let value = result[key]?.length > 0 ? result[key] : [];
      value = Array.isArray(value) ? value : [value];
      if (value?.length > 0) {
        value && value?.forEach((val: string) => updatedURL.append(key, val));
      }
    } else {
      if (typeof result[key] !== "string" && result[key]?.length > 0) {
        updatedURL.set(key, result[key][0]);
      } else if (typeof result[key] === "number" && result[key] > 0) {
        updatedURL.set(key, result[String(key)]);
      } else if (result[key]?.length > 0) {
        updatedURL.set(key, result[key]);
      }
    }
  });
  return updatedURL;
};

export const buildNewURLQueryStringFromReduxState = (
  result: Record<string, any>,
  mode: any = "POINT_IN_TIME",
  pathName?: any,
): URLSearchParams => {
  const updatedURL = new URLSearchParams();
  const currentPathDetail =
    ROUTES_CHILDREN_LOOKUP[pathName ? pathName : location.pathname];

  if (
    location.pathname === `/emotional/${URL_BY_BRAND}` &&
    mode === POINT_IN_TIME &&
    (result?.visualizationType === PERCEPTUAL ||
      result?.visualizationType === BUTTERLFY ||
      result?.visualizationType === DIFFERENTIATION)
  ) {
    mode = result.visualizationType;
  } else if (
    (location.pathname?.startsWith("/emotional") ||
      location.pathname?.startsWith("/advertising") ||
      location.pathname?.startsWith("/equity") ||
      location.pathname?.startsWith("/conversion") ||
      location.pathname?.startsWith("/visibility") ||
      location.pathname?.startsWith("/reputation")) &&
    mode === POINT_IN_TIME &&
    result?.visualizationType === DISTRIBUTION
  ) {
    mode = result.visualizationType;
  }

  currentPathDetail?.urlKeysMapping?.[mode]?.forEach((key: string) => {
    if (key === "questionOptions") {
      const questions: string[] = [];
      Object.keys(result[key] || {})?.map((questionKey: string) => {
        const currentValue = result[key]?.[questionKey];
        currentValue?.forEach((val: number) => {
          questions?.push(`${questionKey}-${val}`);
        });
      });
      questions?.forEach((q: string) =>
        updatedURL?.append("questionOptions", q),
      );
    } else if (currentPathDetail?.multipleOptionsForKeys[mode]?.includes(key)) {
      const value = result[key]?.length > 0 ? result[key] : [];
      if (value?.length > 0) {
        value.forEach((val: string) => updatedURL.append(key, val));
      }
    } else {
      if (typeof result[key] !== "string" && result[key]?.length > 0) {
        updatedURL.append(key, result[key][0]);
      } else if (typeof result[key] === "number" && result[key] > 0) {
        updatedURL.append(key, result[String(key)]);
      } else if (result[key]?.length > 0) {
        updatedURL.append(key, result[key]);
      }
    }
  });
  return updatedURL;
};

/**
 * Constructs a URL from a base, a path, and optional query parameters.
 *
 * @param base The base URL or path segment.
 * @param path The path segment to append.
 * @param params Optional query parameters. `null` and `undefined` values are excluded.
 * @returns The fully constructed URL.
 */
export const buildUrl = (
  base: string,
  path: string,
  params?: Record<
    string,
    string | number | boolean | (string | number)[] | null | undefined
  >,
): string => {
  const combinedPath = `${base.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;

  if (!params || Object.keys(params).length === 0) {
    return combinedPath;
  }

  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined) {
      searchParams.append(key, String(value));
    }
  }

  const queryString = searchParams.toString();
  return queryString ? `${combinedPath}?${queryString}` : combinedPath;
};

/**
 * Parses a single `f=` param value into a `FilterSelection`, per the v2
 * filters URL grammar:
 *   OPTION → `<filterCode>:<optCode>,<optCode>,...`
 *   RANGE  → `<filterCode>:r=<min>-<max>`
 *
 * Load-bearing assumption: filter and option codes are ULIDs (Crockford Base32),
 * never containing `:`, `,`, `=`, or `-`, so the delimiters are unambiguous.
 *
 * Shape-validated: malformed entries (missing code, missing/empty value
 * after the delimiter, non-finite RANGE bounds, or reversed ranges) return
 * `null` and are silently dropped by the caller.
 */
function parseFilterParam(raw: string): FilterSelection | null {
  const separatorIdx = raw.indexOf(":");
  if (separatorIdx <= 0) return null; // no code, or no ":" delimiter at all
  const code = raw.slice(0, separatorIdx);
  const rest = raw.slice(separatorIdx + 1);
  if (!rest) return null;

  if (rest.startsWith("r=")) {
    const rangePart = rest.slice(2);
    const dashIdx = rangePart.indexOf("-");
    if (dashIdx <= 0) return null;
    const min = Number(rangePart.slice(0, dashIdx));
    const max = Number(rangePart.slice(dashIdx + 1));
    if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
    if (min > max) return null; // reject reversed ranges
    return { code, kind: "RANGE", range: { min, max } };
  }

  const options = rest.split(",").filter((opt) => opt !== "");
  if (options.length === 0) return null;
  return { code, kind: "OPTION", options };
}

/**
 * Parse URL search params and convert them to the correct data types.
 */
export const parseURLParams = (
  searchParams: URLSearchParams,
): Record<
  string,
  | string
  | number
  | string[]
  | number[]
  | (number | string)[]
  | FilterSelection[]
> => {
  const result: Record<
    string,
    | string
    | number
    | string[]
    | number[]
    | (number | string)[]
    | FilterSelection[]
  > = {};
  for (const [key] of searchParams.entries()) {
    if (key === QUESTION_OPTIONS) {
      // Parse URL strings of the form "questionKey-optionId" (e.g. "RQ7433-12")
      // into a Record<string, number[]> (e.g. { RQ7433: [12, 13] }) using the
      // LAST dash as the delimiter so question keys that contain dashes are
      // handled correctly.  Empty values are silently ignored.
      const rawValues = searchParams.getAll(key).filter((v) => v !== "");
      const obj: Record<string, number[]> = {};
      rawValues.forEach((item) => {
        const lastDashIdx = item.lastIndexOf("-");
        if (lastDashIdx <= 0) return;
        const questionKey = item.substring(0, lastDashIdx);
        const optionId = parseInt(item.substring(lastDashIdx + 1), 10);
        if (isNaN(optionId)) return;
        if (!obj[questionKey]) obj[questionKey] = [];
        obj[questionKey].push(optionId);
      });
      result[key] = obj as any;
    } else if (key === BRANDS || key === SECONDARY_BRANDS) {
      // Brand sets use string ULIDs; regular brands use numeric IDs.
      // Preserve the original string when it is not a pure integer so that
      // ULID-based brand-set identifiers round-trip correctly through the URL.
      result[key] = searchParams.getAll(key).map((val) => {
        const n = Number(val);
        return Number.isInteger(n) && val !== "" ? n : val;
      });
    } else if (key === SEGMENTS) {
      // v2 segments are ULID string codes — never numeric-coerce (JSON.stringify(NaN) === null on the wire)
      result[key] = searchParams.getAll(key);
    } else if (key === FILTERS) {
      // Legacy `filters=` param (numeric option ids) — no URL grammar exists
      // for FilterSelection[] under this key, so it is silently ignored and
      // never produces a `filters` key on the parsed result (avoiding a
      // numeric-coerced array of garbage ids that could clobber a committed
      // FilterSelection[]).
      continue;
    } else if (key === FILTERS_VERSION_PARAM) {
      // `fv` is a version marker only, consumed inline by the `f=` branch
      // below — it never surfaces on the parsed result.
      continue;
    } else if (key === FILTER_PARAM) {
      // v2 filters URL grammar. `fv=2` is the canonical version marker but
      // `f=` params are parsed regardless of its presence — `parseFilterParam`
      // already validates the format and silently drops malformed entries, so
      // omitting `fv=2` is safe. This allows saved-view URLs that contain
      // `f=` params without `fv=2` to still apply filters correctly.
      const filtersByCode = new Map<string, FilterSelection>();
      searchParams.getAll(key).forEach((raw) => {
        const selection = parseFilterParam(raw);
        if (selection) {
          // LAST-WINS: later `f=` params for the same code replace earlier ones
          filtersByCode.set(selection.code, selection);
        }
      });
      result.filters = Array.from(filtersByCode.values());
    } else if (key === MARKETS || key === QUESTION_OPTIONS) {
      result[key] = searchParams.getAll(key).map((val) => Number(val));
    } else {
      result[key] = searchParams.get(key) as string;
    }
  }
  return result;
};
